import React, { useEffect, useRef, useState } from "react";
import accountDeletionService, { AccountDeletionEligibility, AccountDeletionResult } from "../services/accountDeletionService";
import ConfirmationModal from "./ConfirmationModal";

interface AccountDeletionDialogProps {
  /** Omit for personal deletion: the server always resolves the signed-in actor. */
  targetUserId?: string;
  accountLabel: string;
  appearance?: "default" | "alpha";
  onClose: () => void;
  onDeleted: (result: AccountDeletionResult) => Promise<void>;
}

export default function AccountDeletionDialog({ targetUserId, accountLabel, appearance = "default", onClose, onDeleted }: AccountDeletionDialogProps): React.ReactElement {
  const [eligibility, setEligibility] = useState<AccountDeletionEligibility | null>(null);
  const [checking, setChecking] = useState(true);
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const requestRef = useRef(0);
  const submittingRef = useRef(false);
  const confirmationId = React.useId();
  const personal = targetUserId === undefined;

  useEffect(() => {
    const requestId = ++requestRef.current;
    const controller = new AbortController();
    setEligibility(null);
    setChecking(true);
    setError(null);
    setConfirmation("");
    const request = targetUserId === undefined
      ? accountDeletionService.getPersonalEligibility(controller.signal)
      : accountDeletionService.getAdminEligibility(targetUserId, controller.signal);
    request.then(result => {
      if (requestId === requestRef.current) setEligibility(result);
    }).catch(() => {
      if (requestId === requestRef.current) setError("Could not check deletion availability. Please try again.");
    }).finally(() => {
      if (requestId === requestRef.current) setChecking(false);
    });
    return () => { ++requestRef.current; controller.abort(); };
  }, [targetUserId, attempt]);

  const canDelete = eligibility?.eligible === true && !checking && (!personal || confirmation.trim().toLowerCase() === "delete");

  const deleteAccount = async (): Promise<void> => {
    if (!canDelete || submittingRef.current) return;
    submittingRef.current = true;
    const requestId = requestRef.current;
    setDeleting(true);
    setError(null);
    try {
      const result = targetUserId === undefined
        ? await accountDeletionService.deletePersonalAccount()
        : await accountDeletionService.deleteUser(targetUserId);
      if (requestId !== requestRef.current) return;
      await onDeleted(result);
    } catch (cause) {
      if (requestId !== requestRef.current) return;
      setError(cause instanceof Error ? cause.message : "Could not delete the account. Please try again.");
      // A server-side eligibility change must be checked before another attempt.
      setEligibility(null);
    } finally {
      if (requestId === requestRef.current) {
        submittingRef.current = false;
        setDeleting(false);
      }
    }
  };

  return <ConfirmationModal
    isOpen
    onClose={onClose}
    onConfirm={deleteAccount}
    title={personal ? "Delete your account" : "Delete user"}
    confirmText="Delete account"
    confirmDisabled={!canDelete}
    isDestructive
    loading={deleting}
    loadingText="Deleting..."
    appearance={appearance}
    message={<div className="space-y-4">
      <p>Delete <strong className="break-all">{accountLabel}</strong>? All sessions for this account will end immediately. Only an administrator can restore it.</p>
      {eligibility?.accountKind === "MANAGED" && <p>Deleting this managed account will also sign you out.</p>}
      {checking && <p role="status">Checking deletion availability...</p>}
      {eligibility && !eligibility.eligible && <div role="alert"><p>This account cannot be deleted:</p><ul className="mt-2 list-disc pl-5">{eligibility.blockers.map(blocker => <li key={blocker.code}>{blocker.message}</li>)}</ul></div>}
      {error && <p role="alert" className={appearance === "alpha" ? "text-v7-rust" : "text-red-700 dark:text-red-400"}>{error}</p>}
      {!checking && !eligibility && <button type="button" className="underline" onClick={() => setAttempt(value => value + 1)}>Check again</button>}
      {personal && eligibility?.eligible && <div>
        <label htmlFor={confirmationId} className="mb-2 block">Type <strong>delete</strong> to confirm</label>
        <input id={confirmationId} value={confirmation} onChange={event => setConfirmation(event.target.value)} disabled={deleting} autoComplete="off" spellCheck={false}
          className={appearance === "alpha" ? "w-full rounded-v7-control border border-v7-ink/25 bg-v7-white px-3 py-2 text-v7-ink focus:ring-2 focus:ring-v7-bronze" : "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:ring-2 focus:ring-rp-amber dark:border-rp-separator dark:bg-rp-surface dark:text-white"} />
      </div>}
    </div>}
  />;
}
